import { useState, useMemo, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert, Avatar, Box, Button, Card, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, IconButton, InputAdornment, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Tooltip, Typography,
  TextField as MuiTextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import brokerCompanyApi from '../../services/brokerCompanyApi';
import { useToast } from '../../components/shared/Toast';
import { useConfirmModal } from '../../components/shared/ConfirmModal';
import StatusBadge, { BROKER_STATUS_MAP } from '../../components/shared/StatusBadge';
import PageHeader from '../../components/shared/PageHeader';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';
import FormProvider from '../../components/form/FormProvider';
import TextField from '../../components/form/TextField';
import SelectField from '../../components/form/SelectField';

const createSchema = z.object({
  name: z.string().min(1, 'Company name is required'),
  nmls: z.string().min(1, 'NMLS is required'),
  address: z.object({
    address1: z.string().optional().default(''),
    address2: z.string().optional().default(''),
    city: z.string().optional().default(''),
    state: z.string().max(2).optional().default(''),
    postalCode: z.string().optional().default(''),
  }),
  website: z.string().optional().default(''),
  accountExecutiveId: z.string().optional().default(''),
});

const emptyDefaults = { name: '', nmls: '', address: { address1: '', address2: '', city: '', state: '', postalCode: '' }, website: '', accountExecutiveId: '' };

const BrokersListPage = () => {
  const { data: brokers = [], isLoading } = brokerCompanyApi.endpoints.getBrokerCompanies.useQuery();
  const [createBroker] = brokerCompanyApi.endpoints.createBrokerCompany.useMutation();
  const [deleteBroker] = brokerCompanyApi.endpoints.deleteBrokerCompany.useMutation();
  const { data: accountExecutives = [] } = brokerCompanyApi.endpoints.getAccountExecutives.useQuery();

  const aeOptions = useMemo(() => [
    { value: '', label: 'None' },
    ...accountExecutives.map(ae => ({ value: ae.id, label: ae.fullName })),
  ], [accountExecutives]);

  const { showToast, ToastContainer } = useToast();
  const { confirm, ConfirmModalContainer } = useConfirmModal();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createError, setCreateError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = useCallback((column) => {
    setSortDirection(prev => sortColumn === column ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortColumn(column);
  }, [sortColumn]);

  const processedBrokers = useMemo(() => {
    let list = [...brokers];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(b =>
        b.name?.toLowerCase().includes(term) ||
        b.nmls?.toLowerCase().includes(term) ||
        b.status?.toLowerCase().includes(term) ||
        b.website?.toLowerCase().includes(term)
      );
    }
    list.sort((a, b) => {
      let aVal, bVal;
      switch (sortColumn) {
        case 'name': aVal = (a.name || '').toLowerCase(); bVal = (b.name || '').toLowerCase(); break;
        case 'nmls': aVal = a.nmls || ''; bVal = b.nmls || ''; break;
        case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
        case 'dateCreated': aVal = new Date(a.dateCreated).getTime(); bVal = new Date(b.dateCreated).getTime(); break;
        default: aVal = ''; bVal = '';
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [brokers, searchTerm, sortColumn, sortDirection]);

  const methods = useForm({ resolver: zodResolver(createSchema), defaultValues: emptyDefaults });

  const handleDelete = async (broker) => {
    const confirmed = await confirm({
      title: 'Delete Broker Company',
      message: `Are you sure you want to delete ${broker.name}? This will also remove all users associated with this company. This action cannot be undone.`,
      confirmText: 'Delete',
      confirmClass: 'btn-danger',
    });
    if (!confirmed) return;
    try {
      await deleteBroker(broker.id).unwrap();
      showToast(`${broker.name} has been deleted.`);
    } catch {
      showToast('Failed to delete broker company.', 'danger');
    }
  };

  const handleCreate = async (data) => {
    setCreateError('');
    setSubmitting(true);
    try {
      const payload = { ...data, accountExecutiveId: data.accountExecutiveId || null };
      await createBroker(payload).unwrap();
      setShowCreateModal(false);
      methods.reset(emptyDefaults);
    } catch (err) {
      setCreateError(err.data?.title || 'Failed to create broker company');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenCreate = () => {
    methods.reset(emptyDefaults);
    setCreateError('');
    setShowCreateModal(true);
  };

  if (isLoading) {
    return <LoadingSkeleton variant="page" />;
  }

  return (
    <>
      {ToastContainer}
      {ConfirmModalContainer}

      <PageHeader
        title="Broker Companies"
        subtitle="Manage broker relationships"
        actions={<Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>Add Broker Company</Button>}
      />

      <Card>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5 }}>
          <MuiTextField
            size="small" placeholder="Search brokers..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 280 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {processedBrokers.length} of {brokers.length} companies
          </Typography>
        </Stack>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={sortColumn === 'name' ? sortDirection : false}>
                  <TableSortLabel active={sortColumn === 'name'} direction={sortColumn === 'name' ? sortDirection : 'asc'} onClick={() => handleSort('name')}>Broker Name</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortColumn === 'nmls' ? sortDirection : false}>
                  <TableSortLabel active={sortColumn === 'nmls'} direction={sortColumn === 'nmls' ? sortDirection : 'asc'} onClick={() => handleSort('nmls')}>Main Office NMLS</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortColumn === 'status' ? sortDirection : false}>
                  <TableSortLabel active={sortColumn === 'status'} direction={sortColumn === 'status' ? sortDirection : 'asc'} onClick={() => handleSort('status')}>Status</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortColumn === 'dateCreated' ? sortDirection : false}>
                  <TableSortLabel active={sortColumn === 'dateCreated'} direction={sortColumn === 'dateCreated' ? sortDirection : 'asc'} onClick={() => handleSort('dateCreated')}>Created Date</TableSortLabel>
                </TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processedBrokers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body2" color="text.secondary">{searchTerm ? 'No brokers match your search' : 'No broker companies yet'}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                processedBrokers.map((broker) => (
                  <TableRow key={broker.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{broker.name.charAt(0)}</Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>{broker.name}</Typography>
                          {broker.website && <Typography variant="caption" color="text.secondary">{broker.website}</Typography>}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell><StatusBadge variant="info">{broker.nmls}</StatusBadge></TableCell>
                    <TableCell><StatusBadge status={broker.status} map={BROKER_STATUS_MAP} /></TableCell>
                    <TableCell>{new Date(broker.dateCreated).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View">
                          <IconButton size="small" color="primary" component={RouterLink} to={`/lender/brokers/${broker.id}`}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(broker)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create Broker Modal */}
      <Dialog open={showCreateModal} onClose={() => setShowCreateModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Broker Company</DialogTitle>
        <FormProvider methods={methods} onSubmit={handleCreate}>
          <DialogContent dividers>
            {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
            <TextField name="name" label="Company Name" required size="small" sx={{ mb: 2 }} />
            <TextField name="nmls" label="NMLS" required size="small" sx={{ mb: 2 }} />
            <TextField name="address.address1" label="Street Address" size="small" placeholder="Street address" sx={{ mb: 2 }} />
            <TextField name="address.address2" label="Address Line 2" size="small" placeholder="Suite, unit, etc." sx={{ mb: 2 }} />
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid size={{ xs: 5 }}><TextField name="address.city" label="City" size="small" /></Grid>
              <Grid size={{ xs: 3 }}><TextField name="address.state" label="State" size="small" placeholder="CA" inputProps={{ maxLength: 2 }} /></Grid>
              <Grid size={{ xs: 4 }}><TextField name="address.postalCode" label="Zip Code" size="small" placeholder="92618" /></Grid>
            </Grid>
            <TextField name="website" label="Website" type="url" size="small" placeholder="https://example.com" sx={{ mb: 2 }} />
            <SelectField name="accountExecutiveId" label="Account Executive" options={aeOptions} size="small" />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCreateModal(false)} disabled={submitting}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create'}
            </Button>
          </DialogActions>
        </FormProvider>
      </Dialog>
    </>
  );
};

export default BrokersListPage;
